import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Grid,
  Alert,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Person as PersonIcon,
  Star as StarIcon,
  Assessment as AssessmentIcon,
  CheckCircle as CheckCircleIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { styled } from '@mui/system';
import { useTranslation } from 'react-i18next';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  marginBottom: theme.spacing(2),
}));

const RankingCard = styled(Card)(({ theme, ranking }) => ({
  marginBottom: theme.spacing(2),
  border: ranking === 1 ? `3px solid ${theme.palette.success.main}` :
    ranking === 2 ? `3px solid ${theme.palette.warning.main}` :
      ranking === 3 ? `3px solid ${theme.palette.error.light}` : '1px solid #e0e0e0',
  backgroundColor: ranking === 1 ? theme.palette.success.light + '20' :
    ranking === 2 ? theme.palette.warning.light + '20' :
      ranking === 3 ? theme.palette.error.light + '20' : 'white',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 6px 25px rgba(0,0,0,0.15)',
  },
}));

const AffinityProgress = styled(LinearProgress)(({ theme, value }) => ({
  height: 12,
  borderRadius: 6,
  backgroundColor: theme.palette.grey[200],
  '& .MuiLinearProgress-bar': {
    borderRadius: 6,
    backgroundColor: value >= 80 ? theme.palette.success.main :
      value >= 60 ? theme.palette.warning.main :
        value >= 40 ? theme.palette.info.main :
          theme.palette.error.main,
  },
}));

// NUEVO: Componente estilizado para la sección del candidato ideal con mejor contraste
const IdealCandidateSection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  backgroundColor: theme.palette.info.main, // Cambiar a azul sólido
  borderRadius: theme.spacing(1),
  border: `2px solid ${theme.palette.info.dark}`, // Borde más oscuro
  '& .MuiTypography-root': {
    color: 'white !important', // Texto blanco para contraste
  }
}));

const ComparisonResults = ({
  comparison,
  loading,
  error,
  onRetry,
  onNewComparison
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <StyledPaper>
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" sx={{ mt: 2 }}>
            {t('comparisonDashboard.processingComparison')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('comparisonDashboard.processingText')}
          </Typography>
        </Box>
      </StyledPaper>
    );
  }

  if (error) {
    return (
      <StyledPaper>
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={onRetry}
            >
              {t('retry')}
            </Button>
          }
        >
          <Typography variant="subtitle2">{t('comparisonDashboard.comparisonError')}</Typography>
          <Typography variant="body2">{error}</Typography>
        </Alert>
      </StyledPaper>
    );
  }

  if (!comparison || !comparison.result) {
    return (
      <StyledPaper>
        <Alert severity="info">
          {t('comparisonDashboard.startingProcess')}
        </Alert>
      </StyledPaper>
    );
  }

  const { result } = comparison;
  const { comparison_summary, applicant_results, overall_recommendation } = result;

  // Ordenar candidatos por ranking
  const sortedApplicants = [...(applicant_results || [])].sort((a, b) =>
    (a.ranking_position || 0) - (b.ranking_position || 0)
  );

  const formatDate = (timestamp) => {
    if (!timestamp?._seconds) return t('dateNotAvailable');
    const date = new Date(timestamp._seconds * 1000);
    return date.toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getRankingIcon = (ranking) => {
    switch (ranking) {
      case 1:
        return <StarIcon sx={{ color: 'success.main', fontSize: 30 }} />;
      case 2:
        return <StarIcon sx={{ color: 'warning.main', fontSize: 30 }} />;
      case 3:
        return <StarIcon sx={{ color: 'error.light', fontSize: 30 }} />;
      default:
        return <PersonIcon sx={{ color: 'grey.500', fontSize: 30 }} />;
    }
  };

  const getRankingLabel = (ranking) => {
    switch (ranking) {
      case 1:
        return `🥇 ${t('comparisonDashboard.firstPlace')}`;
      case 2:
        return `🥈 ${t('comparisonDashboard.secondPlace')}`;
      case 3:
        return `🥉 ${t('comparisonDashboard.thirdPlace')}`;
      default:
        return `#${ranking}`;
    }
  };

  // Función para descargar documento Word desde el backend
  const downloadWordDocument = async () => {
    if (!comparison || !comparison.result) {
      alert(t('comparisonDashboard.noResultsAvailable'));
      return;
    }

    const { result } = comparison;
    const { comparison_summary } = result;

    // Verificar si existe el documento Word generado en el backend
    if (!result.wordDocumentUrl) {
      alert(t('reports.layout.wordNotAvailable'));
      return;
    }

    try {
      // Descargar directamente desde el backend
      const link = document.createElement('a');
      link.href = result.wordDocumentUrl;
      link.download = `Resultados_Comparacion_${comparison_summary?.position_name?.replace(/[^a-zA-Z0-9]/g, '_') || 'Postulantes'}_${new Date().toISOString().split('T')[0]}.docx`;
      link.target = '_blank'; // Abrir en nueva pestaña como respaldo

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      console.log('Descargando documento Word desde:', result.wordDocumentUrl);
    } catch (error) {
      console.error('Error al descargar documento Word:', error);
      alert(t('generalApiError'));
    }
  };

  return (
    <Box>
      {/* Header de Resultados */}
      <StyledPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h5" component="h2">
            🏆 {t('comparisonDashboard.comparisonResults')}
          </Typography>
          <Chip
            icon={<CheckCircleIcon />}
            label={t('status.completado')}
            color="success"
            variant="outlined"
          />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('comparisonDashboard.targetPosition')}:
              </Typography>
              <Typography variant="h6" color="primary">
                {comparison_summary?.position_name || 'No especificado'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('comparisonDashboard.comparisonMode')}:
              </Typography>
              <Chip
                label={comparison_summary?.comparison_type === '1vsAll' ? t('comparisonDashboard.1vsAll') : t('comparisonDashboard.allVsAll')}
                color="primary"
                icon={<AssessmentIcon />}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={3}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {t('comparisonDashboard.applicantsAnalized')}:
              </Typography>
              <Typography variant="h4" color="primary">
                {comparison_summary?.total_candidates || 0}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* SECCIÓN CORREGIDA: Candidato ideal con mejor contraste */}
        {comparison_summary?.ideal_candidate && comparison_summary.ideal_candidate !== 'N/A' && (
          <IdealCandidateSection>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', color: 'white !important' }} gutterBottom>
              🎯 {t('comparisonDashboard.idealApplicantReference')}:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'white !important' }}>
              {comparison_summary.ideal_candidate}
            </Typography>
          </IdealCandidateSection>
        )}

        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            {t('processedAt')}: {formatDate(comparison.processed_at)}
          </Typography>
        </Box>
      </StyledPaper>

      {/* Ranking de Candidatos */}
      <StyledPaper>
        <Typography variant="h6" gutterBottom>
          📊 {t('comparisonDashboard.applicantsRanking')}
        </Typography>

        {sortedApplicants.map((applicant, index) => (
          <RankingCard key={index} ranking={applicant.ranking_position}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                {/* Icono de ranking */}
                <Box sx={{ minWidth: 60, textAlign: 'center' }}>
                  {getRankingIcon(applicant.ranking_position)}
                  <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                    {getRankingLabel(applicant.ranking_position)}
                  </Typography>
                </Box>

                {/* Información del candidato */}
                <Box sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                    <Typography variant="h6" component="h3">
                      {applicant.applicant_name}
                    </Typography>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="h4" color="primary" fontWeight="bold">
                        {applicant.affinity_percentage}%
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t('comparisonDashboard.afinity')}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Barra de progreso de afinidad */}
                  <Box sx={{ mb: 2 }}>
                    <AffinityProgress
                      variant="determinate"
                      value={applicant.affinity_percentage}
                    />
                  </Box>

                  {/* Fortalezas clave */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      💪 {t('comparisonDashboard.keyStrengths')}:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {applicant.key_strengths?.map((strength, idx) => (
                        <Chip
                          key={idx}
                          label={strength}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Análisis de ajuste */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      🎯 {t('comparisonDashboard.analisis')}:
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {applicant.job_fit_analysis}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </RankingCard>
        ))}
      </StyledPaper>

      {/* Recomendación General */}
      {overall_recommendation && (
        <StyledPaper>
          <Typography variant="h6" gutterBottom>
            💡 {t('comparisonDashboard.generalRecommendation')}
          </Typography>
          <Alert severity="info">
            <Typography variant="body1">
              {overall_recommendation}
            </Typography>
          </Alert>
        </StyledPaper>
      )}

      {/* Acciones */}
      <StyledPaper>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
          {onNewComparison && (
            <Button
              variant="outlined"
              size="large"
              onClick={onNewComparison}
              startIcon={<RefreshIcon />}
            >
              {t('comparisonDashboard.newComparison')}
            </Button>
          )}
          <Button
            variant="contained"
            size="large"
            onClick={downloadWordDocument}
            startIcon={<DownloadIcon />}
          >
            {t('comparisonDashboard.downloadResults')}
          </Button>
        </Box>
      </StyledPaper>
    </Box>
  );
};

export default ComparisonResults;